import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';

const COLORS = {
  series: '#1e88e5',
  warning: '#ef6c00',
  critical: '#c62828',
  baseline: '#2e7d32',
  grid: '#e8eef2',
  muted: '#78909c',
  plotBg: '#fafcfd',
};

const formatXLabel = (ts, spanMs) => {
  const d = dayjs(ts);
  if (!d.isValid()) return String(ts || '').slice(0, 16);
  if (spanMs >= 12 * 60 * 60 * 1000) return d.format('DD MMM HH:mm');
  return d.format('HH:mm');
};

const pct = (sorted, p) => {
  if (!sorted.length) return 0;
  const i = (sorted.length - 1) * p;
  const lo = Math.floor(i);
  const hi = Math.ceil(i);
  if (lo === hi) return sorted[lo];
  return sorted[lo] * (hi - i) + sorted[hi] * (i - lo);
};

/** Keep extrema per bucket so spikes stay visible without ink soup. */
const downsample = (series, maxPoints) => {
  if (series.length <= maxPoints) return series;
  const buckets = Math.max(2, Math.floor(maxPoints / 2));
  const size = Math.ceil(series.length / buckets);
  const out = [];
  for (let i = 0; i < series.length; i += size) {
    const chunk = series.slice(i, i + size);
    let minP = chunk[0];
    let maxP = chunk[0];
    chunk.forEach((p) => {
      if (p.value < minP.value) minP = p;
      if (p.value > maxP.value) maxP = p;
    });
    if (minP.t <= maxP.t) {
      out.push(minP);
      if (minP !== maxP) out.push(maxP);
    } else {
      out.push(maxP);
      if (minP !== maxP) out.push(minP);
    }
  }
  return out;
};

const severityOf = (v, warn, crit) => {
  if (crit != null && v >= crit) return 'critical';
  if (warn != null && v >= warn) return 'warning';
  return 'normal';
};

const severityStroke = (sev) => {
  if (sev === 'critical') return COLORS.critical;
  if (sev === 'warning') return COLORS.warning;
  return COLORS.series;
};

/** Map mouse → viewBox coords. `stretch` = preserveAspectRatio none (fullscreen fill). */
const clientToViewBox = (svg, clientX, clientY, vbW, vbH, stretch = false) => {
  const rect = svg.getBoundingClientRect();
  if (!rect.width || !rect.height) return null;
  if (stretch) {
    return {
      x: ((clientX - rect.left) / rect.width) * vbW,
      y: ((clientY - rect.top) / rect.height) * vbH,
    };
  }
  const scale = Math.min(rect.width / vbW, rect.height / vbH);
  const offsetX = (rect.width - vbW * scale) / 2;
  const offsetY = (rect.height - vbH * scale) / 2;
  return {
    x: (clientX - rect.left - offsetX) / scale,
    y: (clientY - rect.top - offsetY) / scale,
  };
};

/**
 * Grafana-style sensor chart:
 * - one series color (readable on dense vibration)
 * - green / orange / red only for BASE / WARN / CRIT limits
 * - click uses letterbox-correct coords + 2D nearest (handles same-second duplicates)
 */
const SimpleLineChart = ({
  points = [],
  baseline,
  warningThreshold,
  criticalThreshold,
  unit = '',
  height = 280,
  compact = false,
  /** Stretch plot to the parent box (no letterboxing) — used for fullscreen. */
  fill = false,
  seriesLabel = 'Actual',
  onPointClick,
  selectedTimestamp,
  selectedValue,
}) => {
  const svgRef = useRef(null);
  const plotRef = useRef(null);
  const [plotSize, setPlotSize] = useState({ w: 960, h: height });

  useEffect(() => {
    if (!fill) {
      setPlotSize({ w: 960, h: height });
      return undefined;
    }
    const el = plotRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (!cr) return;
      const w = Math.max(320, Math.floor(cr.width));
      const h = Math.max(200, Math.floor(cr.height));
      setPlotSize((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [fill, height]);

  const width = fill ? plotSize.w : 960;
  const chartH = fill ? plotSize.h : height;
  const padding = compact
    ? { top: 16, right: 72, bottom: 28, left: 48 }
    : { top: 22, right: 78, bottom: 36, left: 56 };

  const built = useMemo(() => {
    const full = (points || [])
      .map((p, idx) => {
        const t = dayjs(p?.timestamp).valueOf();
        const value = Number(p?.value);
        return { timestamp: p?.timestamp, t, value, raw: p, idx };
      })
      .filter((p) => Number.isFinite(p.t) && Number.isFinite(p.value))
      .sort((a, b) => a.t - b.t || a.idx - b.idx);

    if (!full.length) return null;

    const bl = baseline != null && baseline !== '' ? Number(baseline) : null;
    const warn = warningThreshold != null && warningThreshold !== '' ? Number(warningThreshold) : null;
    const crit = criticalThreshold != null && criticalThreshold !== '' ? Number(criticalThreshold) : null;
    const thrNums = [bl, warn, crit].filter((v) => v != null && Number.isFinite(v));

    const display = downsample(full, fill ? 900 : compact ? 320 : 480);
    const sortedVals = [...full.map((p) => p.value)].sort((a, b) => a - b);

    let minV = pct(sortedVals, 0.05);
    let maxV = pct(sortedVals, 0.95);
    thrNums.forEach((v) => {
      minV = Math.min(minV, v);
      maxV = Math.max(maxV, v);
    });
    if (minV === maxV) {
      minV -= 0.5;
      maxV += 0.5;
    }
    const thrSpan = thrNums.length >= 2 ? Math.max(...thrNums) - Math.min(...thrNums) : 0;
    const minSpan = Math.max(thrSpan * 8, 0.35, (maxV - minV) * 0.25);
    if (maxV - minV < minSpan) {
      const mid = (minV + maxV) / 2;
      minV = mid - minSpan / 2;
      maxV = mid + minSpan / 2;
    }
    const yPad = (maxV - minV) * 0.12;
    minV -= yPad;
    maxV += yPad;
    const ySpan = maxV - minV || 1;

    const innerW = width - padding.left - padding.right;
    const innerH = chartH - padding.top - padding.bottom;
    const tMin = full[0].t;
    const tMax = full[full.length - 1].t;
    const spanMs = Math.max(1, tMax - tMin);

    const xAtT = (t) => padding.left + ((t - tMin) / spanMs) * innerW;
    const yAt = (v) => padding.top + innerH - ((v - minV) / ySpan) * innerH;
    const yClamp = (v) => yAt(Math.min(maxV, Math.max(minV, v)));

    // Single continuous path (Grafana default) — thresholds carry the color meaning
    const lineD = display
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAtT(p.t).toFixed(1)} ${yClamp(p.value).toFixed(1)}`)
      .join(' ');

    const ticks = [0, 0.25, 0.5, 0.75, 1].map((frac) => {
      const v = minV + ySpan * frac;
      return { y: yAt(v), label: v.toFixed(2) };
    });

    const labelCount = spanMs >= 36 * 60 * 60 * 1000 ? 5 : 4;
    const xLabels = [];
    for (let i = 0; i < labelCount; i += 1) {
      const t = tMin + (i / Math.max(labelCount - 1, 1)) * spanMs;
      xLabels.push({ x: xAtT(t), label: formatXLabel(t, spanMs) });
    }

    const bands = [];
    if (warn != null && Number.isFinite(warn)) {
      const top = crit != null && Number.isFinite(crit) ? crit : maxV;
      if (top > warn) {
        bands.push({
          key: 'w',
          y: Math.min(yAt(top), yAt(warn)),
          h: Math.abs(yAt(warn) - yAt(top)),
          color: COLORS.warning,
          opacity: 0.06,
        });
      }
    }
    if (crit != null && Number.isFinite(crit) && crit < maxV) {
      bands.push({
        key: 'c',
        y: yAt(maxV),
        h: Math.abs(yAt(maxV) - yAt(crit)),
        color: COLORS.critical,
        opacity: 0.07,
      });
    }

    const limits = [
      { key: 'bl', num: bl, color: COLORS.baseline, dash: '8 5', tag: 'BASE' },
      { key: 'wn', num: warn, color: COLORS.warning, dash: '6 4', tag: 'WARN' },
      { key: 'cr', num: crit, color: COLORS.critical, dash: '4 3', tag: 'CRIT' },
    ]
      .filter((l) => l.num != null && Number.isFinite(l.num))
      .map((l) => ({ ...l, y: yAt(l.num) }));

    const hitPoints = full.map((p) => ({
      x: xAtT(p.t),
      y: yClamp(p.value),
      timestamp: p.timestamp,
      value: p.value,
      raw: p.raw,
    }));

    return { lineD, ticks, xLabels, bands, limits, hitPoints, warn, crit };
  }, [
    points,
    baseline,
    warningThreshold,
    criticalThreshold,
    width,
    chartH,
    compact,
    fill,
    padding.left,
    padding.right,
    padding.top,
    padding.bottom,
  ]);

  const selectedHit = useMemo(() => {
    if (!built?.hitPoints?.length || selectedTimestamp == null) return null;
    const target = dayjs(selectedTimestamp).valueOf();
    if (!Number.isFinite(target)) return null;
    const wantVal =
      selectedValue != null && selectedValue !== '' && Number.isFinite(Number(selectedValue))
        ? Number(selectedValue)
        : null;

    let best = null;
    let bestScore = Infinity;
    built.hitPoints.forEach((h) => {
      const dt = Math.abs(dayjs(h.timestamp).valueOf() - target);
      const dv = wantVal != null ? Math.abs(h.value - wantVal) : 0;
      // Prefer exact time; break ties with value when many readings share a second
      const score = dt + dv * 1e-6;
      if (score < bestScore) {
        bestScore = score;
        best = h;
      }
    });
    return best;
  }, [built, selectedTimestamp, selectedValue]);

  const pickNearest = useCallback((localX, localY) => {
    if (!built?.hitPoints?.length) return null;
    // Weight Y so clicking a spike tip beats a same-time trough; still prioritize time
    const xWeight = 1;
    const yWeight = 0.35;
    let best = null;
    let bestScore = Infinity;
    built.hitPoints.forEach((h) => {
      const dx = h.x - localX;
      const dy = h.y - localY;
      const score = dx * dx * xWeight + dy * dy * yWeight;
      if (score < bestScore) {
        bestScore = score;
        best = h;
      }
    });
    return best;
  }, [built]);

  const handleSvgClick = useCallback(
    (e) => {
      if (!onPointClick || !built?.hitPoints?.length || !svgRef.current) return;
      const local = clientToViewBox(svgRef.current, e.clientX, e.clientY, width, chartH, fill);
      if (!local) return;
      if (local.x < padding.left - 10 || local.x > width - padding.right + 10) return;
      if (local.y < padding.top - 10 || local.y > chartH - padding.bottom + 10) return;
      const best = pickNearest(local.x, local.y);
      if (!best) return;
      onPointClick({
        timestamp: best.timestamp,
        value: best.value,
        ...(best.raw && typeof best.raw === 'object' ? best.raw : {}),
      });
    },
    [onPointClick, built, padding.left, padding.right, padding.top, padding.bottom, width, chartH, fill, pickNearest]
  );

  if (!built) {
    return (
      <Box
        sx={{
          height: fill ? '100%' : height,
          minHeight: fill ? 200 : undefined,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography color="text.secondary" variant="body2">
          No chart data for this range. Click Apply to load.
        </Typography>
      </Box>
    );
  }

  const { lineD, ticks, xLabels, bands, limits, warn, crit } = built;
  const selectedSev = selectedHit ? severityOf(selectedHit.value, warn, crit) : 'normal';
  const selectedColor = severityStroke(selectedSev);

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box
        ref={plotRef}
        sx={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          bgcolor: COLORS.plotBg,
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        <svg
          ref={svgRef}
          viewBox={`0 0 ${width} ${chartH}`}
          width="100%"
          height="100%"
          preserveAspectRatio={fill ? 'none' : 'xMidYMid meet'}
          role="img"
          aria-label="Sensor time series"
          onClick={handleSvgClick}
          style={{ cursor: onPointClick ? 'crosshair' : 'default', display: 'block' }}
        >
          <rect
            x={padding.left}
            y={padding.top}
            width={width - padding.left - padding.right}
            height={chartH - padding.top - padding.bottom}
            fill={COLORS.plotBg}
          />

          {ticks.map((t) => (
            <g key={`g-${t.label}`}>
              <line
                x1={padding.left}
                x2={width - padding.right}
                y1={t.y}
                y2={t.y}
                stroke={COLORS.grid}
                strokeWidth="1"
              />
              <text x={padding.left - 6} y={t.y + 3} textAnchor="end" fontSize="10" fill={COLORS.muted}>
                {t.label}
              </text>
            </g>
          ))}

          {bands.map((b) => (
            <rect
              key={b.key}
              x={padding.left}
              y={b.y}
              width={width - padding.left - padding.right}
              height={Math.max(b.h, 0)}
              fill={b.color}
              opacity={b.opacity}
            />
          ))}

          <path
            d={lineD}
            fill="none"
            stroke={COLORS.series}
            strokeWidth={compact ? 1.45 : 1.7}
            strokeLinejoin="round"
            strokeLinecap="round"
            pointerEvents="none"
          />

          {limits.map((l) => (
            <g key={l.key}>
              <line
                x1={padding.left}
                x2={width - padding.right - 44}
                y1={l.y}
                y2={l.y}
                stroke="#ffffff"
                strokeWidth="4"
                opacity="0.9"
              />
              <line
                x1={padding.left}
                x2={width - padding.right - 44}
                y1={l.y}
                y2={l.y}
                stroke={l.color}
                strokeDasharray={l.dash}
                strokeWidth="2.2"
              />
              <rect
                x={width - padding.right - 42}
                y={l.y - 8}
                width={40}
                height={14}
                rx="3"
                fill="#fff"
                stroke={l.color}
                strokeWidth="1"
              />
              <text
                x={width - padding.right - 22}
                y={l.y + 3}
                textAnchor="middle"
                fontSize="8"
                fontWeight="700"
                fill={l.color}
              >
                {l.tag}
              </text>
            </g>
          ))}

          {selectedHit && (
            <g pointerEvents="none">
              <line
                x1={selectedHit.x}
                x2={selectedHit.x}
                y1={padding.top}
                y2={chartH - padding.bottom}
                stroke="#607d8b"
                strokeDasharray="3 3"
                strokeWidth="1"
              />
              <circle
                cx={selectedHit.x}
                cy={selectedHit.y}
                r={5.5}
                fill="#fff"
                stroke={selectedColor}
                strokeWidth="2.5"
              />
            </g>
          )}

          {xLabels.map((l) => (
            <text
              key={`${l.x}-${l.label}`}
              x={l.x}
              y={chartH - 8}
              textAnchor="middle"
              fontSize="10"
              fill={COLORS.muted}
            >
              {l.label}
            </text>
          ))}
          {unit ? (
            <text x={padding.left} y={padding.top - 3} fontSize="10" fill={COLORS.muted}>
              {unit}
            </text>
          ) : null}
        </svg>
      </Box>

      <Stack
        direction="row"
        spacing={1.5}
        flexWrap="wrap"
        useFlexGap
        sx={{ px: 0.5, pt: 0.5, flexShrink: 0, alignItems: 'center' }}
      >
        <Typography variant="caption" sx={{ fontSize: '0.68rem', color: COLORS.series, fontWeight: 600 }}>
          ― {seriesLabel}
        </Typography>
        {baseline != null && (
          <Typography variant="caption" sx={{ fontSize: '0.68rem', color: COLORS.baseline }}>
            - - Baseline {Number(baseline).toFixed(3)}
          </Typography>
        )}
        {warningThreshold != null && (
          <Typography variant="caption" sx={{ fontSize: '0.68rem', color: COLORS.warning }}>
            - - Warning {Number(warningThreshold).toFixed(3)}
          </Typography>
        )}
        {criticalThreshold != null && (
          <Typography variant="caption" sx={{ fontSize: '0.68rem', color: COLORS.critical }}>
            - - Critical {Number(criticalThreshold).toFixed(3)}
          </Typography>
        )}
        {onPointClick && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
            Crosshair: click nearest reading
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export default SimpleLineChart;
